import { useState, useEffect } from "react";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { UserAvatar } from "@components/ui/UserAvatar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import SearchField from "@components/ui/textfields/SearchField";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { usersApi } from "@services/api";
import { showNotification } from "context/notification.service";
import SendPushModal from "@components/modals/SendPushModal";
import type { User } from "types";

export const Users = () => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null);
  const [pushTarget, setPushTarget] = useState<User | null>(null);
  const loginAs = useAuthStore((state) => state.loginAs);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const returnToAdmin = useAuthStore((state) => state.returnToAdmin);
  const userData = useDataStore((state) => state.userData);
  const clearAllData = useDataStore((state) => state.clearAllData);

  useEffect(() => {
    const fetchAllUsers = async () => {
      setLoading(true);
      try {
        const users = await usersApi.list({ limit: 1000 });
        setAllUsers(users);
      } catch (error) {
        console.error("Failed to fetch all users:", error);
        showNotification({ type: "error", message: "Impossible de charger les utilisateurs" });
      } finally {
        setLoading(false);
      }
    };
    fetchAllUsers();
  }, []);

  const handleLoginAs = async (targetUid: string) => {
    setLoginAsLoading(targetUid);
    try {
      clearAllData();
      await loginAs(targetUid);
      showNotification({ type: "success", message: "Connexion effectuée" });
    } catch (error: any) {
      console.error("Login as failed:", error);
      showNotification({ type: "error", message: "Impossible de se connecter en tant que cet utilisateur" });
    } finally {
      setLoginAsLoading(null);
    }
  };

  const handleReturnToAdmin = async () => {
    try {
      clearAllData();
      await returnToAdmin();
      showNotification({ type: "success", message: "Retour au compte admin" });
    } catch (error) {
      console.error("Return to admin failed:", error);
      showNotification({ type: "error", message: "Impossible de revenir au compte admin" });
    }
  };

  const filteredUsers = allUsers
    .filter((u) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        u.infos.display_name?.toLowerCase().includes(s) ||
        u.infos.full_name?.toLowerCase().includes(s) ||
        u.infos.email?.toLowerCase().includes(s) ||
        u.org_id?.toLowerCase().includes(s)
      );
    })
    .sort((a, b) =>
      (a.infos.full_name || a.infos.display_name || "").localeCompare(
        b.infos.full_name || b.infos.display_name || "",
      ),
    );

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-900">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Utilisateurs</Text1>
          <Text2>Tous les utilisateurs de la plateforme.</Text2>
          <Text3>
            Connectez-vous en tant qu'un autre utilisateur pour diagnostiquer des
            problèmes ou effectuer des actions en son nom.
          </Text3>
        </div>

        {isImpersonating && (
          <div className="flex flex-row items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <div className="text-h3 font-medium text-amber-800">
              Vous êtes connecté en tant que {userData?.infos.full_name || userData?.infos.display_name || userData?.infos.email}
            </div>
            <SecondaryButton
              variant="small"
              label="Retour au compte admin"
              onClick={handleReturnToAdmin}
            />
          </div>
        )}

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border">
          <div className="flex flex-row justify-between items-center mb-2">
            <div className="text-h2 font-semibold text-black">
              Utilisateurs ({filteredUsers.length})
            </div>
          </div>

          <SearchField
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Rechercher par nom, email ou org_id..."
          />

          {loading ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Chargement des utilisateurs...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-h4 text-neutral-400 text-center py-8">
              Aucun utilisateur trouvé
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-neutral-200 max-h-[70vh] overflow-y-auto">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex flex-row justify-between items-center py-3 px-2"
                >
                  <div className="flex flex-row items-center gap-3 flex-1 min-w-0">
                    <UserAvatar
                      src={user.customization?.photo_url}
                      className="w-10 h-10 rounded-full border flex-shrink-0"
                      value={user.customization?.avatar_seed || user.infos.display_name}
                      size={40}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-h3 font-medium truncate">
                        {user.infos.full_name || user.infos.display_name}
                        {user.id === userData?.id && (
                          <span className="text-h4 text-neutral-400 font-normal ml-2">
                            (Vous)
                          </span>
                        )}
                      </div>
                      <div className="text-h4 text-neutral-400 truncate">
                        {user.infos.email}
                      </div>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 mr-4">
                      <div className="text-h5 text-neutral-300 font-mono">
                        {user.org_id}
                      </div>
                      <div className={`text-h5 ${user.status === "active" ? "text-emerald-500" : user.status === "pending" ? "text-amber-500" : "text-neutral-400"}`}>
                        {user.status || "active"}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2">
                    <SecondaryButton
                      variant="small"
                      label="Push"
                      onClick={() => setPushTarget(user)}
                    />
                    <SecondaryButton
                      variant="small"
                      label="Login as"
                      disabled={user.id === userData?.id || loginAsLoading !== null}
                      onClick={() => handleLoginAs(user.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pushTarget && (
        <SendPushModal
          user={pushTarget}
          onClose={() => setPushTarget(null)}
        />
      )}
    </div>
  );
};
